<template>
    <div class="ppv2-flex ppv2-flex-col ppv2-gap-8">
        <!-- Info Banner -->
        <!-- TODO: Uncomment when InfoBanner.vue is available -->
        <!-- <InfoBanner :text="$t('website_settings.faq.info_banner')" class="ppv2-mb-8" /> -->

        <form @submit.prevent="save">
            <SectionCard title="FAQ" :hideToggle="true">
                <div class="ppv2-flex ppv2-flex-col ppv2-gap-8 ppv2-mt-4 ppv2-p-4">
                    <!-- FAQ Items -->
                    <div
                        v-for="(faq, index) in localFaqs"
                        :key="faq.id || index"
                        class="ppv2-relative ppv2-group"
                        :style="{ paddingTop: normalizeSize(16, true) }"
                    >
                        <!-- Action Buttons (Floating Top Right) -->
                        <div class="ppv2-absolute ppv2-top-0 ppv2-right-0 ppv2-flex ppv2-gap-2 ppv2-z-10">
                            <div
                                class="ppv2-bg-white ppv2-bg-opacity-5 border_btns ppv2-opacity-75 ppv2-text-black ppv2-text-opacity-90 d-flex flex-wrap align-items-center justify-center px-2"
                                style="width: 26px; height: 26px"
                            >
                                <button
                                    type="button"
                                    class="transparent-btn ppv2-text-black disabled:ppv2-opacity-30"
                                    @click="moveFaq(index, -1)"
                                    :disabled="index === 0"
                                    :title="$t('website_settings.faq.move_up')"
                                >
                                    <ArrowUp :size="15" />
                                </button>
                            </div>

                            <div
                                class="ppv2-bg-white ppv2-bg-opacity-5 border_btns ppv2-opacity-75 ppv2-text-black ppv2-text-opacity-90 d-flex flex-wrap align-items-center justify-center px-2"
                                style="width: 26px; height: 26px"
                            >
                                <button
                                    type="button"
                                    class="transparent-btn ppv2-text-black disabled:ppv2-opacity-30"
                                    @click="moveFaq(index, 1)"
                                    :disabled="index === localFaqs.length - 1"
                                    :title="$t('website_settings.faq.move_down')"
                                >
                                    <ArrowDown :size="15" />
                                </button>
                            </div>

                            <div
                                class="ppv2-bg-white ppv2-bg-opacity-5 border_btns ppv2-opacity-75 ppv2-text-black ppv2-text-opacity-90 d-flex flex-wrap align-items-center justify-center px-2"
                                style="width: 26px; height: 26px"
                            >
                                <button
                                    type="button"
                                    class="transparent-btn ppv2-text-black"
                                    @click="removeFaq(index)"
                                    :title="$t('website_settings.faq.remove')"
                                >
                                    <Trash2 :size="15" />
                                </button>
                            </div>
                        </div>

                        <!-- Question -->
                        <div class="ppv2-mb-6">
                            <CustomInput
                                v-model="faq.faq"
                                :label="$t('website_settings.faq.question_label_numbered', { n: index + 1 })"
                                :required="true"
                                customLabelClass="poppinsLight"
                                :placeholder="$t('website_settings.faq.question_placeholder')"
                                :errorText="errors['faq_input_text.' + index] ? errors['faq_input_text.' + index][0] : ''"
                            />
                        </div>

                        <!-- Answer -->
                        <div>
                            <BaseTextarea
                                v-model="faq.content"
                                :label="$t('website_settings.faq.answer_label_numbered', { n: index + 1 })"
                                :required="true"
                                customLabelClass="poppinsLight"
                                :use-editor="true"
                                :placeholder="$t('website_settings.faq.answer_placeholder')"
                                :rows="4"
                                :error="errors['faq_description.' + index]"
                            />
                        </div>

                        <!-- Separator line if not last -->
                        <div
                            v-if="index !== localFaqs.length - 1"
                            class="ppv2-border-b ppv2-border-black ppv2-opacity-60 ppv2-my-8"
                        ></div>
                    </div>

                    <!-- Centered Add Button -->
                    <div class="ppv2-flex ppv2-justify-center ppv2-mt-4">
                        <BaseButton @click="addFaq" variant="secondary" :customFontSize="12">
                            <Plus :size="12" />
                            {{ $t('website_settings.faq.add_question_button') }}
                        </BaseButton>
                    </div>

                    <!-- AI Helper Button (Secondary) -->
                    <div class="ppv2-flex ppv2-justify-center ppv2-mt-2">
                        <button
                            type="button"
                            class="ppv2-text-primary-500 hover:ppv2-underline ppv2-text-xs ppv2-flex ppv2-items-center ppv2-gap-1"
                            @click="showAiHelper = !showAiHelper"
                        >
                            <WandSparkles :size="12" />
                            {{ $t('website_settings.faq.ai_helper_button') }}
                        </button>
                    </div>

                    <!-- AI Helper Panel (Integrated) -->
                    <transition name="fade">
                        <div
                            v-if="showAiHelper"
                            class="ppv2-p-4 ppv2-rounded-xl ppv2-bg-gray-50 ppv2-border ppv2-border-gray-200 ppv2-mt-4"
                        >
                            <div class="ppv2-flex ppv2-flex-col" :style="{ gap: normalizeSize(16, true) }">
                                <BaseLabel :label="$t('website_settings.faq.ai_helper_label')" customClass="poppinsMedium" />
                                <div class="ppv2-flex ppv2-gap-2">
                                    <div class="ppv2-flex-1">
                                        <CustomInput
                                            v-model="aiPrompt"
                                            :placeholder="$t('website_settings.faq.ai_helper_placeholder')"
                                        />
                                    </div>
                                    <BaseButton
                                        type="button"
                                        variant="primary"
                                        :loading="aiLoading"
                                        @click="generateAiFaqs"
                                        :disabled="!aiPrompt.trim()"
                                        customClass="!ppv2-min-w-[150px]"
                                    >
                                        {{ $t('website_settings.faq.ai_generate_button') }}
                                    </BaseButton>
                                </div>
                            </div>
                        </div>
                    </transition>
                </div>
            </SectionCard>
        </form>
    </div>
</template>

<script>
import CustomInput from '../../../../Inputs/CustomInput.vue'
import BaseTextarea from '../../../../Shared/BaseTextarea.vue'
import BaseButton from '../../../../Shared/BaseButton.vue'
import SectionCard from './SectionCard.vue'
import BaseLabel from '../../../../Shared/BaseLabel.vue'
// TODO: Uncomment when InfoBanner.vue is available
// import InfoBanner from '../../../../Shared/InfoBanner.vue'
import { WandSparkles, Bot, ArrowUp, ArrowDown, Trash2, Plus, Info } from 'lucide-vue'
import normalizeSize from '../../../../../utils/normalizeSize'

export default {
    name: 'TabFaq',
    components: {
        CustomInput,
        BaseTextarea,
        BaseButton,
        SectionCard,
        BaseLabel,
        WandSparkles,
        Bot,
        ArrowUp,
        ArrowDown,
        Trash2,
        Plus,
        Info,
        // TODO: Uncomment when InfoBanner.vue is available
        // InfoBanner,
    },
    props: {
        data: { type: Object, default: () => ({}) },
    },
    data() {
        return {
            saving: false,
            localFaqs: [],
            showAiHelper: false,
            aiPrompt: '',
            aiLoading: false,
            errors: {},
        }
    },
    mounted() {
        this.initializeForm()
    },
    activated() {
        this.$root.$off('website-settings-trigger-save-faq', this.save)
        this.$root.$on('website-settings-trigger-save-faq', this.save)
    },
    deactivated() {
        this.$root.$off('website-settings-trigger-save-faq', this.save)
    },
    beforeDestroy() {
        this.$root.$off('website-settings-trigger-save-faq', this.save)
    },
    methods: {
        normalizeSize,
        initializeForm() {
            const faqs = this.data?.faqs || []

            if (faqs.length > 0) {
                this.localFaqs = faqs.map((f, idx) => ({
                    id: f.id || idx,
                    faq: f.faq || '',
                    content: f.content || '',
                }))
            }
        },
        addFaq() {
            this.localFaqs.push({
                id: Date.now(),
                faq: '',
                content: '',
            })
        },
        removeFaq(index) {
            this.localFaqs.splice(index, 1)
        },
        moveFaq(index, direction) {
            const newIndex = index + direction
            if (newIndex < 0 || newIndex >= this.localFaqs.length) return

            const temp = this.localFaqs[index]
            this.$set(this.localFaqs, index, this.localFaqs[newIndex])
            this.$set(this.localFaqs, newIndex, temp)
        },
        async generateAiFaqs() {
            if (!this.aiPrompt.trim()) return

            this.aiLoading = true
            try {
                const response = await axios.post('/chat-research-faq', {
                    prompt: this.aiPrompt,
                    type: 'faq',
                })

                if (response.data && Array.isArray(response.data.faqs)) {
                    response.data.faqs.forEach((aiFaq) => {
                        this.localFaqs.push({
                            id: Date.now() + Math.random(),
                            faq: aiFaq.question || aiFaq.faq || '',
                            content: aiFaq.answer || aiFaq.content || '',
                        })
                    })

                    this.$toast.success(this.$t('website_settings.faq.ai_success'))
                    this.showAiHelper = false
                    this.aiPrompt = ''
                } else {
                    this.$toast.warning(this.$t('website_settings.faq.ai_empty'))
                }
            } catch (error) {
                if (error.response && error.response.status === 422) {
                    this.errors = error.response.data.errors
                    this.$toast.error(this.$t('website_settings.faq.ai_error'))
                } else {
                    this.$toast.error(this.$t('website_settings.faq.ai_error'))
                }
            } finally {
                this.aiLoading = false
            }
        },
        async save() {
            this.errors = {}
            this.saving = true
            try {
                const formData = new FormData()
                this.localFaqs.forEach((faq, index) => {
                    formData.append(`faq_input_text[${index}]`, faq.faq)
                    formData.append(`faq_description[${index}]`, faq.content)
                })

                const saveResponse = await axios.post('/back-office/website/faqs-settings/store', formData)
                this.$emit('save', { loading: false, success: true, message: saveResponse.data?.message })
            } catch (error) {
                this.$toast.error(error.response.data.message)
                if (error.response && error.response.status === 422) {
                    this.errors = error.response.data.errors
                    this.$toast.error(this.$t('common.error_validate') || 'Erreur de validation')
                } else {
                    this.$toast.error(this.$t('website_settings.actions.save_error') || "Erreur lors de l'enregistrement")
                }
            } finally {
                this.saving = false
            }
        },
    },
}
</script>

<style scoped>
.section-block {
    border-radius: 20px;
    padding: 24px;
    box-shadow:
        0 4px 6px -1px rgba(0, 0, 0, 0.1),
        0 2px 4px -1px rgba(0, 0, 0, 0.06);
    border: 1px solid rgba(0, 0, 0, 0.05);
}

.dark .section-block {
    border-color: rgba(255, 255, 255, 0.05);
}

.transparent-btn {
    width: 100% !important;
    height: 100% !important;
    box-shadow: none !important;
    position: absolute;
    left: 0;
    top: 0;
    padding: 0 !important;
    border: none;
    min-width: auto !important;
    background: transparent !important;
}
.border_btns {
    position: relative;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    border-radius: 7px;
    border: 1px solid #545595;
    min-height: 26px;
    overflow: hidden;
}
.transparent-btn:hover {
    background: #d9d9d91a !important;
}
.transparent-btn-icon {
    box-shadow: none !important;
    min-width: auto !important;
}
</style>
